import { Component, input, OnInit, computed, Signal } from "@angular/core";
import { BehaviorSubject, combineLatest, filter, finalize, of, take } from "rxjs";
import { formatFromDateWithSeconds } from "src/app/common/datetime_utility";
import { findingAttackToDisplayName } from "src/app/common/findingRulesUtils";
import { anyOwaspToDisplayName, getviolationRuleRiskReasonCardFieldKey, getViolationRuleToOwaspLinks } from "src/app/common/violationRulesUtils";
import { ISecurityTag } from "src/app/components/security-tag/security-tag.component";
import { ISidepanelTab } from "src/app/components/sidepanel-tabs/sidepanel-tabs.component";
import { ITimelineItem } from "src/app/components/timeline/timeline.component";
import { IntegrationModel, ResourceType } from "src/app/models/api";
import { AgentClientApplicationIcon, AgentStepModel, AiService, StepType } from "src/app/models/api/agentStep.model";
import { FindingModel } from "src/app/models/api/finding.model";
import { AiFindingsTableRowViewModel } from "src/app/models/viewModels/aiFindingsTableRowViewModel";
import { BeautifyDisplayStringPipe } from "src/app/pipes/beautify-display-string/beautify-display-string.pipe";
import { DisplayCamelCaseStringPipe } from "src/app/pipes/display-camel-case-string/display-camel-case-string.pipe";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import {
    AiAgentStepsHttpService,
    SensitiveDataRequestActions,
} from "src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service";
import { AiFindingsHttpService } from "src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service";
import { HttpErrorHandlerService } from "src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service";
import { StoreService } from "src/app/services/infra/store/store.service";
import { SidePanelService } from "../../../features/side-panel/services/side-panel.service";
import { Router } from "@angular/router";
import { ICardField } from "src/app/models/viewModels/cardModel";
import { TemplateRef, viewChild } from "@angular/core";
import { isM365CustomAgent, getAiServiceLogoPath } from "src/app/common/aiServiceUtils";

@Component({
    selector: "app-finding",
    templateUrl: "./enterprise-ai-finding.component.html",
    styleUrls: ["./enterprise-ai-finding.component.scss"],
})
export class EnterpriseAiFindingComponent implements OnInit {
    integration = input.required<string>();
    finding = input.required<string>();
    viewModel = input<AiFindingsTableRowViewModel | null>(null);

    public errorMessage = "";
    public isLoading$ = new BehaviorSubject<boolean>(true);
    public findingModel: FindingModel;
    public integrationName: string;
    public showNoData = false;
    public tabs: ISidepanelTab[];
    public activeTabIndex = 0;
    public findingDetailsCardFields: ICardField[];
    public actorCardFields: ICardField[];
    public relevantStepsCardFieldList: ICardField[][];
    public owaspRiskTag: ISecurityTag[] = [];
    public attackRiskTag: ISecurityTag[] = [];
    public triageSuggestion: ITimelineItem[] = [];
    public fixSuggestion: ITimelineItem[] = [];

    public customAgentTemplate = viewChild("customAgentTemplate", { read: TemplateRef });

    constructor(
        private _storeService: StoreService,
        private _aiFindingsService: AiFindingsHttpService,
        private _aiAgentStepsService: AiAgentStepsHttpService,
        private _httpErrorHandlerService: HttpErrorHandlerService,
        private _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
        private _labelsProviderPipe: LabelsProviderPipe,
        private _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
        private _sidePanel: SidePanelService,
        private _router: Router,
    ) {}

    public ngOnInit() {
        let finding = this.viewModel()?.finding ?? null;
        const finding$ = finding ? of(finding) : this._aiFindingsService.getAiFinding(this.integration(), this.finding());

        combineLatest([finding$, this._storeService.integrations$])
            .pipe(
                take(1),
                filter(([finding]) => {
                    if (!finding) {
                        console.warn("Finding not found in the server. Not showing any data");
                        this.showNoData = true;
                        return false;
                    }
                    return true;
                }),
                finalize(() => this.isLoading$.next(false)),
            )
            .subscribe({
                next: ([finding, integrations]) => {
                    this.initialize(finding, integrations);
                },
                error: (error) => {
                    if (error.status === 404) this.showNoData = true;
                    else this._httpErrorHandlerService.handle(error);
                },
            });
    }

    public onTabSelect(index: number) {
        this.activeTabIndex = index;
    }

    public openStepDetails(step: AgentStepModel) {
        this._sidePanel.openAgentStepDetailsFromFinding(step.thread.id, step.id, step);
    }

    private async initialize(finding: FindingModel, integrations: IntegrationModel[]): Promise<void> {
        const selectedIntegration: IntegrationModel | undefined = integrations.find((integration) => integration.id === this.integration());
        this.integrationName = selectedIntegration?.name ?? "";

        this.findingModel = finding;
        this.showNoData = false;
        this.tabs = [{ labelPath: "inventoryResourcePage.overviewTabTitle" }];

        this.findingDetailsCardFields = this.getFindingDetailsFields();
        this.relevantStepsCardFieldList = this.getRelevantStepsFields();
        this.actorCardFields = this.getActorFields();

        this.owaspRiskTag = this.findingModel.rule.owasp.map((owasp) => ({
            key: this._labelsProviderPipe.transform(getviolationRuleRiskReasonCardFieldKey(owasp)),
            value: this._beautifyDisplayStringPipe.transform(anyOwaspToDisplayName[owasp]),
            link: getViolationRuleToOwaspLinks(owasp),
        }));

        this.attackRiskTag = this.findingModel.rule.genAiAttack.map((attack) => ({
            key: this._labelsProviderPipe.transform("violationPage.riskReasonCardAttackFieldKey"),
            value: this._beautifyDisplayStringPipe.transform(findingAttackToDisplayName[attack.toUpperCase()]),
        }));

        this.findingModel.rule.triageSuggestion.split(/\r?\n/).forEach((element) => this.triageSuggestion.push({ text: element }));
        this.findingModel.rule.fixSuggestion.split(/\r?\n/).forEach((element) => this.fixSuggestion.push({ text: element }));
    }

    private getFindingDetailsFields(): ICardField[] {
        const relatedStep = this.findingModel.relatedSteps[0];
        const isCustomM365Agent = isM365CustomAgent(relatedStep.service, relatedStep.botDetails.displayName);

        const agentNameField: ICardField = {
            key: this._labelsProviderPipe.transform("aiFindingPage.agentNameLabel"),
        };

        // Use custom template for custom M365 agents, regular display for others
        if (isCustomM365Agent) {
            agentNameField.customTemplate = this.customAgentTemplate();
        } else {
            agentNameField.value = relatedStep.botDetails.displayName;
            agentNameField.iconPath = `assets/images/logo/${relatedStep.service}.svg`;
            if (relatedStep.service === AiService.copilotStudio) {
                agentNameField.method = () => {
                    this._router.navigateByUrl(
                        `app/inventory?q=type:${ResourceType.copilot} search:"${relatedStep.botDetails.platformInfo.resourceId}"`,
                    );
                };
            }
        }

        return [
            {
                key: this._labelsProviderPipe.transform("aiFindingPage.ruleIdLabel"),
                value: this.findingModel.rule.id,
                copyableText: this.findingModel.rule.id,
                isCopyable: true,
            },
            {
                key: this._labelsProviderPipe.transform("aiFindingPage.threadIdLabel"),
                value: this.findingModel.thread.id,
                copyableText: this.findingModel.thread.id,
                isCopyable: true,
            },
            {
                key: this._labelsProviderPipe.transform("aiFindingPage.aiServiceLabel"),
                value: this._displayCamelCaseStringPipe.transform(this.findingModel.rule.service),
                iconPath: `assets/images/logo/${this.findingModel.rule.service}.svg`,
            },
            {
                key: this._labelsProviderPipe.transform("aiFindingPage.clientApplicationLabel"),
                value: this._displayCamelCaseStringPipe.transform(relatedStep.clientContext.clientApplication),
                iconPath: Object.values(AgentClientApplicationIcon).includes(
                    relatedStep.clientContext.clientApplication.toString() as AgentClientApplicationIcon,
                )
                    ? `assets/images/logo/agent-client-app-${relatedStep.clientContext.clientApplication}.svg`
                    : "assets/images/logo/agent-client-app-other.svg",
            },
            agentNameField,
            {
                key: this._labelsProviderPipe.transform("aiAgentStepPage.environmentNameFieldName"),
                value: relatedStep?.environment?.displayName ?? "",
                copyableText: relatedStep?.environment?.displayName ?? "",
                isCopyable: true,
            },
        ];
    }

    private getRelevantStepsFields(): ICardField[][] {
        return this.findingModel.relatedSteps.map((step) => {
            const fields: ICardField[] = [];
            fields.push({
                key: this._labelsProviderPipe.transform("aiFindingPage.stepTypeLabel"),
                value: this._displayCamelCaseStringPipe.transform(step.type),
            });
            fields.push({
                key: this._labelsProviderPipe.transform("aiFindingPage.timestampFieldName"),
                value: formatFromDateWithSeconds(step.timestamp),
            });
            fields.push({
                key: this._labelsProviderPipe.transform("aiFindingPage.stepDetailsLabel"),
                value: this._labelsProviderPipe.transform("aiFindingPage.linkToStepDetails"),
                method: () => this.openStepDetails(step),
            });
            if (step.clientContext.region)
                fields.push({
                    key: this._labelsProviderPipe.transform("aiFindingPage.regionLabel"),
                    value: step.clientContext.region,
                });
            return fields.concat(this._getSpecificStepFields(step));
        });
    }

    private getActorFields(): ICardField[] {
        return [
            {
                key: this._labelsProviderPipe.transform("aiFindingPage.nameLabel"),
                value: this.findingModel.mainActor.displayName,
            },
            {
                key: this._labelsProviderPipe.transform("aiFindingPage.emailLabel"),
                value: this.findingModel.mainActor.email ?? "N/A",
            },
        ];
    }

    public getAiServiceIconPath(): string {
        if (!this.findingModel) return "";
        return getAiServiceLogoPath(this.findingModel.relatedSteps[0].service);
    }

    private _getSpecificStepFields(step: AgentStepModel): ICardField[] {
        const fields: ICardField[] = [];
        switch (step.type) {
            case StepType.aiMessageStep:
            case StepType.userMessageStep:
                fields.push({
                    key: this._labelsProviderPipe.transform("aiAgentStepPage.messageFieldName"),
                    value: "",
                    isRawValue: true,
                    sensitiveBtnText: this._labelsProviderPipe.transform("aiAgentStepPage.showPromptContentLinkName"),
                    showPopupBtn: true,
                    sensitiveDataMethod: () =>
                        this._aiAgentStepsService.getAgentStepSensitiveData(this.integration(), step.id, SensitiveDataRequestActions.transcript),
                });
                break;
            case StepType.ragStep:
                switch (step.service) {
                    case AiService.m365Copilot:
                        if (step.sensitivityLabelId)
                            fields.push({
                                key: this._labelsProviderPipe.transform("aiAgentStepPage.sensitivityLabelFieldName"),
                                value: step.sensitivityLabelId,
                                isCopyable: true,
                                copyableText: step.sensitivityLabelId,
                            });
                        break;
                }
        }

        return fields;
    }
}
